<template>
  <m-focusable
    ref="wrapper"
    :small="small"
    :show-placeholder="!focused && !hidePlaceholder && placeholder !== '' && value === ''"
    :full-width="fullWidth"
    :hide-border="hideBorder"
    :hide-hover="hideHover"
    :read-only="readOnly"
    :placeholder-icon="placeholderIcon"
    :placeholder="placeholder"
    :class="classes"
    :disabled="disabled"
    :focused="focused"
    :m-style="mStyle"
    no-padding
    wrap-placeholder
    :placeholder-style="placeholderStyle"
    @click="handleClick"
  >
    <textarea
      ref="textarea"
      :placeholder="placeholder"
      :rows="rows"
      :value="value"
      :disabled="disabled"
      :readonly="readOnly"
      :class="['_input']"
      :style="resolveStyles({ ...defaultStyle, ...mStyle })"
      @input="update"
      @blur="blur"
      @focus="$emit('focus', $event)"
    />
    <m-btn
      v-if="clearable"
      class="_clear-btn"
      icon="close-circle"
      fab
      light
      small
      hide-border
      @click="clear"
    />
  </m-focusable>
</template>

<script>
import { isNullOrUndefined } from 'shared/lib/object/object';
import { mStyleProps, resolveStyles } from 'shared/lib/m-style-props';
import { replaceArrows } from 'shared/lib/arrow-replacement';

export default {
  name: 'MTextarea',
  props: {
    ...mStyleProps,
    value: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    hideHover: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    placeholderIcon: {
      type: String,
      default: '',
    },
    hidePlaceholder: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    hideBorder: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    wrap: {
      type: Boolean,
      default: false,
    },
    autoSize: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    xs: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 1,
    },
    enableArrowReplacement: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:value', 'change', 'blur', 'focus', 'click'],
  data() {
    return {
      height: 0,
      focused: false,
      resizeObserver: null,
    };
  },
  computed: {
    defaultStyle() {
      if (this.xs) {
        return { fontStyle: { fontSize: 'small' } };
      }
      return {};
    },
    placeholderStyle() {
      if (this.small) {
        return { minHeight: `${(this.rows * 18) / 10}rem` };
      }
      return { minHeight: `${(this.rows * 21) / 10}rem` };
    },
    classes() {
      return [
        'm-textarea',
        this.disabled ? '-disabled' : '',
        this.fullWidth ? '-full-width' : '',
        this.hideBorder ? '-hide-border' : '',
        this.hideHover ? '-hide-hover' : '',
        this.readOnly ? '-read-only' : '',
        this.autoSize ? '-auto-size' : '',
        this.disabled ? '-no-resize' : '',
        this.small ? '-small' : '',
        this.xs ? '-xs' : '',
        this.wrap ? '' : '-nowrap',
      ];
    },
  },
  methods: {
    resolveStyles,
    handleClick(event) {
      this.$emit('click', event);
      this.focus();
    },
    initResizeObserver() {
      if (!this.autoSize || isNullOrUndefined(this.$refs.textarea)) {
        return;
      }

      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this.$refs.textarea);
      this.$nextTick(() => this.resize());
    },
    resize() {
      if (!this.autoSize || isNullOrUndefined(this.$refs.textarea)) {
        return;
      }

      if (this.value === '') {
        this.$refs.textarea.style.height = 'unset';
        return;
      }

      this.$refs.textarea.style.height = 'auto';
      this.$nextTick(() => {
        const scrollHeight = this.$refs.textarea.scrollHeight;
        this.$refs.textarea.style.height = `${scrollHeight}px`;
        this.$refs.wrapper.$el.style.height = `${scrollHeight}px`;
      });
    },
    update(event) {
      let value = event.target.value;
      const textarea = event.target;

      if (this.enableArrowReplacement) {
        const caretPos = textarea.selectionStart;
        const result = replaceArrows(value, caretPos);
        if (result !== null) {
          value = result.text;
          textarea.value = value;
          const newStart = result.adjustPosition(textarea.selectionStart);
          const newEnd = result.adjustPosition(textarea.selectionEnd);
          textarea.setSelectionRange(newStart, newEnd);
        }
      }

      this.$emit('change', value);
      this.$emit('input', value);
      this.$emit('update:value', value);
      this.resize();
    },
    blur(event) {
      this.focused = false;
      this.$emit('blur', event);
    },
    focus(options) {
      this.focused = true;
      this.$nextTick(() => {
        if (isNullOrUndefined(this.$refs.textarea)) {
          return;
        }
        this.$refs.textarea.focus(options);
      });
    },
    clear() {
      this.$emit('change', '');
      this.$emit('input', '');
      this.$emit('update:value', '');
      this.resize();
    },
  },
  mounted() {
    this.initResizeObserver();
    if (this.autoFocus) {
      // FIXME: We have the next tick so auto-focus work within a dropdown or modal
      // Ideally, we should expose focus and callers deal with rendering lifecycle complications they introduced
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.focus();
        });
      });
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .m-textarea {
    position: relative;

    ._input {
      width: 100%;
      height: 100%;
      padding: .3rem 1rem;
      cursor: inherit;
      resize: vertical;
      border: none;
      border-radius: $input-field-border-radius;
      outline: none;

      &:focus,
      &:focus-visible {
        outline: none;
      }

      &::placeholder {
        color: $font-color-tertiary;
      }
    }

    &.-small {
      ._input {
        padding: .2rem .6rem;
      }
    }

    &.-xs {
      ._input {
        padding: .2rem .4rem;
      }
    }

    &.-nowrap {
      ._input {
        white-space: nowrap;
      }
    }

    &.-auto-size {
      ._input {
        overflow: hidden;
        resize: none;
      }
    }

    &.-no-resize {
      ._input {
        resize: none;
      }
    }

    &.-read-only {
      ._input {
        color: $font-color-primary;
        cursor: default;
      }
    }

    ._clear-btn {
      position: absolute;
      top: .4rem;
      right: .4rem;
      z-index: 1;
      display: none;
    }

    &:hover {
      &:not(.-disabled) {
        ._clear-btn {
          display: flex;
        }
      }
    }
  }
</style>
